import { defineMessages, translate, type MessageDef, type TParams } from '@/shared/i18n/define';
import { unidadDe } from './catalog';
import { formatBs, formatDate, formatPct, formatUsd } from './format';
import { fuenteLabel, regionCorta, serieLabel, unidadLabel } from './labels';
import type { Alerta, Lang, Notificacion } from './types';
import type { ResultadoEvaluacion } from './alertas';

/** Textos de alertas (descripción, detalle de una prueba y notificación local). Puro: sin React ni store. */
const msgs = defineMessages(
  'alertasTexto',
  {
    bcv: 'Dólar BCV',
    min: '{sujeto} (mínimo)',
    max: '{sujeto} (máximo)',
    en: '{sujeto} en {region}',
    fuente: '{sujeto} · {fuente}',
    sobre: '{sujeto} ≥ {valor}',
    bajo: '{sujeto} ≤ {valor}',
    sube: '{sujeto} sube ≥ {pct} en {count} día|{sujeto} sube ≥ {pct} en {count} días',
    baja: '{sujeto} baja ≥ {pct} en {count} día|{sujeto} baja ≥ {pct} en {count} días',
    tituloSube: '{sujeto} sube a {valor}',
    tituloBaja: '{sujeto} baja a {valor}',
    tituloSubePct: '{sujeto} sube {pct}',
    tituloBajaPct: '{sujeto} baja {pct}',
    cuerpoSobre: 'Superó tu umbral de {umbral}{origen}.',
    cuerpoBajo: 'Cayó por debajo de tu umbral de {umbral}{origen}.',
    cuerpoPct:
      'Cambió {pct} en {count} día (umbral {umbral}){origen}.|Cambió {pct} en {count} días (umbral {umbral}){origen}.',
    detalleSinDatos: 'No hay datos que cumplan el filtro.',
    detalleSinPrevio:
      'Todavía no hay un dato de hace {count} día para comparar.|Todavía no hay un dato de hace {count} días para comparar.',
    detalleInactiva: 'La alerta está pausada.',
    detalleValor: 'Valor actual: {valor} ({fecha}).',
    detalleCambio: 'Valor actual: {valor} ({fecha}); cambio de {pct}.',
    cumple: 'Se cumple la condición.',
    cumpleCooldown: 'Se cumple, pero ya avisó según su frecuencia.',
    noCumple: 'Aún no se cumple.',
  },
  {
    bcv: 'BCV dollar',
    min: '{sujeto} (minimum)',
    max: '{sujeto} (maximum)',
    en: '{sujeto} in {region}',
    fuente: '{sujeto} · {fuente}',
    sobre: '{sujeto} ≥ {valor}',
    bajo: '{sujeto} ≤ {valor}',
    sube: '{sujeto} rises ≥ {pct} in {count} day|{sujeto} rises ≥ {pct} in {count} days',
    baja: '{sujeto} falls ≥ {pct} in {count} day|{sujeto} falls ≥ {pct} in {count} days',
    tituloSube: '{sujeto} rises to {valor}',
    tituloBaja: '{sujeto} falls to {valor}',
    tituloSubePct: '{sujeto} up {pct}',
    tituloBajaPct: '{sujeto} down {pct}',
    cuerpoSobre: 'It went above your threshold of {umbral}{origen}.',
    cuerpoBajo: 'It fell below your threshold of {umbral}{origen}.',
    cuerpoPct:
      'It changed {pct} in {count} day (threshold {umbral}){origen}.|It changed {pct} in {count} days (threshold {umbral}){origen}.',
    detalleSinDatos: 'No data matches the filter.',
    detalleSinPrevio:
      'There is no data from {count} day ago to compare yet.|There is no data from {count} days ago to compare yet.',
    detalleInactiva: 'The alert is paused.',
    detalleValor: 'Current value: {valor} ({fecha}).',
    detalleCambio: 'Current value: {valor} ({fecha}); change of {pct}.',
    cumple: 'The condition is met.',
    cumpleCooldown: 'The condition is met, but it already notified according to its frequency.',
    noCumple: 'Not met yet.',
  },
);

const def = msgs as unknown as MessageDef;
const t = (lang: Lang, key: string, params?: TParams): string => translate(def, lang, key, params);

function unidadDeAlerta(a: Alerta, lang: Lang): string {
  if (a.objetivo === 'bcv' || !a.filtro) return '';
  return `/${unidadLabel(unidadDe(a.filtro.rubro, a.filtro.categoria), lang)}`;
}

function sujeto(a: Alerta, lang: Lang): string {
  if (a.objetivo === 'bcv' || !a.filtro) return t(lang, 'bcv');
  const f = a.filtro;
  let texto = serieLabel(f, lang);
  if (a.metrica !== 'prom') texto = t(lang, a.metrica, { sujeto: texto });
  if (f.region) texto = t(lang, 'en', { sujeto: texto, region: regionCorta(f.region, lang) });
  if (f.fuente) texto = t(lang, 'fuente', { sujeto: texto, fuente: fuenteLabel(f.fuente, lang) });
  return texto;
}

/** Monto en la moneda de la alerta: `$2,80/kg`, `Bs. 2.463,00/kg`, `Bs. 905,00` (dólar BCV). */
function monto(a: Alerta, n: number | null, lang: Lang): string {
  const base =
    a.objetivo === 'bcv' || a.moneda === 'VES' ? formatBs(n, { lang }) : formatUsd(n, { lang });
  return base + unidadDeAlerta(a, lang);
}

/** "Toro en pie ≥ $2,80/kg" · "Dólar BCV sube ≥ 2,0 % en 7 días". */
export function describirAlerta(alerta: Alerta, lang: Lang = 'es'): string {
  const base = { sujeto: sujeto(alerta, lang), count: alerta.periodoDias };
  const pct = formatPct(alerta.umbral, { lang });
  switch (alerta.condicion) {
    case 'sobre':
    case 'bajo':
      return t(lang, alerta.condicion, { ...base, valor: monto(alerta, alerta.umbral, lang) });
    case 'sube_pct':
      return t(lang, 'sube', { ...base, pct });
    case 'baja_pct':
      return t(lang, 'baja', { ...base, pct });
  }
}

/** Frase para el botón "Probar": qué valor hay hoy y si la condición se cumple. */
export function detalleEvaluacion(a: Alerta, r: ResultadoEvaluacion, lang: Lang = 'es'): string {
  if (r.motivo === 'inactiva') return t(lang, 'detalleInactiva');
  if (r.motivo === 'sin_datos') return t(lang, 'detalleSinDatos');
  if (r.motivo === 'sin_previo') return t(lang, 'detalleSinPrevio', { count: a.periodoDias });
  const fecha = formatDate(r.fechaDato, 'corta', lang);
  const valor = monto(a, r.valor, lang);
  const dato =
    r.difPct === null
      ? t(lang, 'detalleValor', { valor, fecha })
      : t(lang, 'detalleCambio', { valor, fecha, pct: formatPct(r.difPct, { lang, sign: true }) });
  const estado =
    r.motivo === 'dispara' ? 'cumple' : r.motivo === 'cooldown' ? 'cumpleCooldown' : 'noCumple';
  return `${dato} ${t(lang, estado)}`;
}

export type NotificacionNueva = Omit<Notificacion, 'id' | 'origen'>;

/** Enlace interno de la app para abrir el dato que disparó la alerta. */
function enlaceDe(a: Alerta): string {
  if (a.objetivo === 'bcv' || !a.filtro) return '/calculadora?tab=conversor';
  const q = new URLSearchParams({ rubro: a.filtro.rubro, categoria: a.filtro.categoria });
  if (a.filtro.presentacion) q.set('presentacion', a.filtro.presentacion);
  return `/precios?${q.toString()}`;
}

/** Notificación local de una alerta que disparó (título y cuerpo en el idioma del usuario). */
export function notificacionDeAlerta(
  a: Alerta,
  r: ResultadoEvaluacion,
  lang: Lang,
  creada: string,
): NotificacionNueva {
  const s = sujeto(a, lang);
  const pct = formatPct(r.difPct === null ? null : Math.abs(r.difPct), { lang });
  const pctFirmado = formatPct(r.difPct, { lang, sign: true });
  const titulos = {
    sobre: 'tituloSube',
    bajo: 'tituloBaja',
    sube_pct: 'tituloSubePct',
    baja_pct: 'tituloBajaPct',
  } as const;
  const titulo = t(lang, titulos[a.condicion], { sujeto: s, valor: monto(a, r.valor, lang), pct });
  const origenTexto = [
    r.fila ? fuenteLabel(r.fila.fuente, lang) : null,
    r.fila?.region ? regionCorta(r.fila.region, lang) : null,
  ]
    .filter(Boolean)
    .join(', ');
  const origen = origenTexto ? ` (${origenTexto})` : '';
  const umbral = esPctCond(a) ? formatPct(a.umbral, { lang }) : monto(a, a.umbral, lang);
  const cuerpoClave =
    a.condicion === 'sobre' ? 'cuerpoSobre' : a.condicion === 'bajo' ? 'cuerpoBajo' : 'cuerpoPct';
  return {
    tipo: 'alerta',
    titulo,
    cuerpo: t(lang, cuerpoClave, { umbral, origen, pct: pctFirmado, count: a.periodoDias }),
    datos: {
      alertaId: a.id,
      valor: r.valor,
      umbral: a.umbral,
      fechaDato: r.fechaDato,
      fuente: r.fila?.fuente ?? null,
      region: r.fila?.region ?? null,
    },
    enlace: enlaceDe(a),
    creada,
    leida: false,
  };
}

const esPctCond = (a: Alerta): boolean => a.condicion === 'sube_pct' || a.condicion === 'baja_pct';
